import { useCallback, useEffect, useRef, useState } from 'react';

// Voice input: a port of the widget's (../ultimo-bots-widget/src/index.js, "Voice input"). The microphone records
// (MediaRecorder), the bar shows the live wave, "done" sends the recording to the backend
// (controller/voice_input.py) and hands the text to the input field; the visitor reads it and sends it. Shown only
// when config.voice_input is true (the backend's effective value: switched on AND a plan from Boost up) and the
// browser can record. At most two minutes; the microphone is released as soon as a recording ends.

const MAX_MS = 120000;

const TEXT = {
  en: { start: 'Speak your message', cancel: 'Cancel', done: 'Done', recording: 'Recording', working: 'Transcribing…',
        blocked: 'Microphone access is blocked', nothing: 'Nothing heard, please try again', failed: 'Voice input is not available right now' },
  de: { start: 'Nachricht einsprechen', cancel: 'Abbrechen', done: 'Fertig', recording: 'Aufnahme läuft', working: 'Wird umgewandelt…',
        blocked: 'Mikrofon-Zugriff ist blockiert', nothing: 'Nichts gehört, bitte noch einmal versuchen', failed: 'Spracheingabe ist gerade nicht verfügbar' },
  fr: { start: 'Dicter votre message', cancel: 'Annuler', done: 'Terminé', recording: 'Enregistrement', working: 'Transcription…',
        blocked: 'L’accès au micro est bloqué', nothing: 'Rien entendu, veuillez réessayer', failed: 'La saisie vocale n’est pas disponible pour le moment' },
  it: { start: 'Detta il tuo messaggio', cancel: 'Annulla', done: 'Fatto', recording: 'Registrazione', working: 'Trascrizione…',
        blocked: 'L’accesso al microfono è bloccato', nothing: 'Non ho sentito nulla, riprova', failed: 'L’input vocale non è disponibile al momento' },
  es: { start: 'Dicta tu mensaje', cancel: 'Cancelar', done: 'Listo', recording: 'Grabando', working: 'Transcribiendo…',
        blocked: 'El acceso al micrófono está bloqueado', nothing: 'No se oyó nada, inténtalo de nuevo', failed: 'La entrada de voz no está disponible ahora' },
  pt: { start: 'Dite a sua mensagem', cancel: 'Cancelar', done: 'Concluído', recording: 'A gravar', working: 'A transcrever…',
        blocked: 'O acesso ao microfone está bloqueado', nothing: 'Nada ouvido, tente novamente', failed: 'A entrada de voz não está disponível agora' },
  nl: { start: 'Spreek je bericht in', cancel: 'Annuleren', done: 'Klaar', recording: 'Opname loopt', working: 'Wordt omgezet…',
        blocked: 'Toegang tot de microfoon is geblokkeerd', nothing: 'Niets gehoord, probeer het opnieuw', failed: 'Spraakinvoer is nu niet beschikbaar' },
  pl: { start: 'Nagraj wiadomość', cancel: 'Anuluj', done: 'Gotowe', recording: 'Nagrywanie', working: 'Przetwarzanie…',
        blocked: 'Dostęp do mikrofonu jest zablokowany', nothing: 'Nic nie usłyszano, spróbuj ponownie', failed: 'Wprowadzanie głosowe jest teraz niedostępne' },
};

export function voiceText() {
  const langs = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language];
  const hit = langs.map((l) => String(l || '').slice(0, 2).toLowerCase()).find((l) => TEXT[l]);
  return TEXT[hit] || TEXT.en;
}

// A secure page, the MediaRecorder API, and a page (or iframe) that may use the microphone; otherwise no microphone.
export function voiceSupported() {
  try {
    if (!window.isSecureContext || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || !window.MediaRecorder) return false;
    const policy = document.permissionsPolicy || document.featurePolicy;
    return !(policy && policy.allowsFeature && !policy.allowsFeature('microphone'));
  } catch {
    return false;
  }
}

// `onText(text)`: the transcription, for the input field. Returns the state ('idle' | 'recording' | 'working'), the
// actions, the canvas ref for the wave and a short notice for the input's placeholder (blocked, nothing heard, ...).
export function useVoiceInput({ botId, enabled, onText }) {
  const [state, setState] = useState('idle');
  const [notice, setNotice] = useState('');
  const canvasRef = useRef(null);
  const rec = useRef(null);
  // A second click while the browser still asks for the microphone must not start a second recorder (it kept the
  // microphone open unseen, 03.11.2026).
  const starting = useRef(false);
  const noticeTimer = useRef(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  const text = useRef(voiceText()).current;
  const supported = !!enabled && voiceSupported();

  const say = useCallback((message) => {
    setNotice(message);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 4000);
  }, []);

  const release = (v) => {
    clearTimeout(v.timer);
    cancelAnimationFrame(v.raf);
    v.stream.getTracks().forEach((track) => track.stop());
    if (v.ctx) v.ctx.close().catch(() => {});
  };

  // A row of thin bars, newest on the right; a dot where it is quiet.
  const draw = useCallback(() => {
    const v = rec.current;
    const canvas = canvasRef.current;
    if (!v || !canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth * ratio;
    const height = canvas.clientHeight * ratio;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    if (v.analyser) {
      const samples = new Uint8Array(v.analyser.fftSize);
      v.analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i += 1) sum += ((samples[i] - 128) / 128) ** 2;
      v.levels.push(Math.min(1, Math.sqrt(sum / samples.length) * 4));
    } else {
      v.levels.push(0);
    }
    const step = 4 * ratio;
    const count = Math.floor(width / step);
    if (v.levels.length > count) v.levels.splice(0, v.levels.length - count);
    const g = canvas.getContext('2d');
    g.clearRect(0, 0, width, height);
    g.fillStyle = '#4a4a55';
    v.levels.forEach((level, i) => {
      const h = Math.max(2 * ratio, level * height);
      g.fillRect(width - (v.levels.length - i) * step, (height - h) / 2, 2 * ratio, h);
    });
    v.raf = requestAnimationFrame(draw);
  }, []);

  // Also while transcribing: the request is dropped, the input keeps what was there.
  const cancel = useCallback(() => {
    const v = rec.current;
    if (!v) return;
    rec.current = null;
    v.cancelled = true;
    if (v.abort) v.abort.abort();
    try {
      v.recorder.onstop = null;
      if (v.recorder.state !== 'inactive') v.recorder.stop();
    } catch { /* already stopped */ }
    release(v);
    setState('idle');
  }, []);

  const finish = useCallback(() => {
    const v = rec.current;
    if (!v || v.finishing) return;
    v.finishing = true;
    clearTimeout(v.timer);
    v.recorder.onstop = async () => {
      release(v);
      const seconds = (Date.now() - v.started) / 1000;
      const blob = new Blob(v.chunks, { type: v.recorder.mimeType || (v.chunks[0] && v.chunks[0].type) || 'audio/webm' });
      setState('working');
      let result = '';
      let message = text.failed;
      try {
        const form = new FormData();
        form.append('audio', blob, 'voice');
        const abort = new AbortController();
        v.abort = abort;
        const timer = setTimeout(() => abort.abort(), 60000);
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}voice/transcribe/${encodeURIComponent(botId)}?seconds=${seconds.toFixed(1)}`,
          { method: 'POST', body: form, signal: abort.signal }).finally(() => clearTimeout(timer));
        if (res.ok) result = String((await res.json()).text || '').trim();
        if (res.ok || res.status === 400) message = text.nothing;
      } catch { /* the notice says it */ }
      if (v.cancelled) return;
      if (rec.current === v) rec.current = null;
      setState('idle');
      if (result) onTextRef.current(result);
      else say(message);
    };
    try {
      if (v.recorder.state !== 'inactive') v.recorder.stop();
      else v.recorder.onstop();
    } catch {
      v.recorder.onstop();
    }
  }, [botId, say, text]);

  const start = useCallback(async () => {
    if (rec.current || starting.current || !supported) return;
    starting.current = true;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (err) {
      say(err && (err.name === 'NotAllowedError' || err.name === 'SecurityError') ? text.blocked : text.failed);
      return;
    } finally {
      starting.current = false;
    }
    const type = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm']
      .find((t) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t));
    let recorder;
    try {
      recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      say(text.failed);
      return;
    }
    const v = { stream, recorder, chunks: [], levels: [], started: Date.now() };
    rec.current = v;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) v.chunks.push(e.data); };
    recorder.start(250);
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      v.ctx = new Ctx();
      v.analyser = v.ctx.createAnalyser();
      v.analyser.fftSize = 512;
      v.ctx.createMediaStreamSource(stream).connect(v.analyser);
    } catch { /* no wave, recording still works */ }
    v.timer = setTimeout(finish, MAX_MS);
    setState('recording');
  }, [supported, say, text, finish]);

  // The wave starts once the bar (and its canvas) is on screen.
  useEffect(() => {
    if (state !== 'recording' || !rec.current) return undefined;
    rec.current.raf = requestAnimationFrame(draw);
    return () => { if (rec.current) cancelAnimationFrame(rec.current.raf); };
  }, [state, draw]);

  // Never keep the microphone open behind a closed page.
  useEffect(() => () => { cancel(); clearTimeout(noticeTimer.current); }, [cancel]);

  return { supported, state, start, cancel, finish, canvasRef, notice, text };
}
